import React, { createContext, use, useCallback, useMemo, useState } from 'react';

import type { Page, Popup } from '../types/entities';
import type { Category, MenuItem, Position } from '../types/shared';

// Types
interface Menus {
    headerTopMenu?: MenuItem[];
    headerMainMenu?: MenuItem[];
    socialMenu?: MenuItem[];
    footerMenu?: MenuItem[];
}

interface SiteState {
    pages: Record<string, Page>;
    popup: Popup | null;
    categories: Category[];
    menus: Menus;
    lavalPosition: Position;
    newArticle: Page | null;
    lastLocation: string | null;
    statusCode: number | null;
    showCookieConsent: boolean;
}

interface SiteContextValue extends SiteState {
    setPage: (url: string, page: Page) => void;
    setStatusCode: (code: number | null) => void;
    setNewArticle: (article: Page | null) => void;
    setShowCookieConsent: (show: boolean) => void;
    setLastLocation: (location: string | null) => void;
    setAnimationComplete: (complete: boolean) => void;
    animationComplete: boolean;
}

// Context
const SiteContext = createContext<SiteContextValue | undefined>(undefined);

// Provider Props
interface SiteProviderProps {
    children: React.ReactNode;
    pages: Record<string, Page>;
    popup: Popup | null;
    categories: Category[];
    menus: Menus;
    lavalPosition: Position;
    newArticle: Page | null;
    lastLocation: string | null;
    statusCode: number | null;
    showCookieConsent: boolean;
    animationComplete: boolean;
}

// Provider Component
export const SiteProvider: React.FC<SiteProviderProps> = ({
    children,
    pages: initialPages,
    popup: initialPopup,
    categories: initialCategories,
    menus: initialMenus,
    lavalPosition: initialLavalPosition,
    newArticle: initialNewArticle,
    lastLocation: initialLastLocation,
    statusCode: initialStatusCode,
    showCookieConsent: initialShowCookieConsent,
    animationComplete: initialAnimationComplete,
}) => {
    const [pages, setPages] = useState<Record<string, Page>>(initialPages || {});
    const [popup, setPopup] = useState(initialPopup ?? null);
    const [newArticle, setNewArticle] = useState(initialNewArticle ?? null);
    const [lastLocation, setLastLocation] = useState(initialLastLocation ?? null);
    const [statusCode, setStatusCode] = useState<number | null>(initialStatusCode ?? null);
    const [showCookieConsent, setShowCookieConsent] = useState<boolean>(
        initialShowCookieConsent ?? false,
    );

    const categories = useMemo(() => initialCategories || [], [initialCategories]);
    const menus = useMemo(() => initialMenus || {}, [initialMenus]);
    const lavalPosition = useMemo<Position>(
        () =>
            initialLavalPosition || {
                latitude: 45.583729,
                longitude: -73.750069,
            },
        [initialLavalPosition],
    );

    const setPage = useCallback(
        (url: string, page: Page) => {
            setStatusCode(null);
            setPages((prev) => ({
                ...prev,
                [url]: page,
            }));
        },
        [setStatusCode, setPages],
    );

    const handleSetLastLocation = useCallback(
        (location: string | null) => {
            setLastLocation(location);
            setStatusCode(null);
        },
        [setLastLocation, setStatusCode],
    );

    const [animationComplete, setAnimationComplete] = useState(initialAnimationComplete ?? false);

    const value = useMemo(
        () => ({
            pages,
            popup,
            categories,
            menus,
            lavalPosition,
            newArticle,
            lastLocation,
            statusCode,
            showCookieConsent,
            setPage,
            setStatusCode,
            setNewArticle,
            setShowCookieConsent,
            setLastLocation: handleSetLastLocation,
            animationComplete,
            setAnimationComplete,
            setPopup,
        }),
        [
            pages,
            popup,
            categories,
            menus,
            lavalPosition,
            newArticle,
            lastLocation,
            statusCode,
            showCookieConsent,
            setPage,
            setStatusCode,
            setNewArticle,
            setShowCookieConsent,
            handleSetLastLocation,
            animationComplete,
            setAnimationComplete,
            setPopup,
        ],
    );

    return <SiteContext value={value}>{children}</SiteContext>;
};

// Hook
export const useSite = (): SiteContextValue => {
    const context = use(SiteContext);
    if (context === undefined) {
        throw new Error('useSite must be used within a SiteProvider');
    }
    return context;
};

export default SiteContext;
